import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField, InputAdornment, Chip,
  Tabs, Tab, Tooltip, Button,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Clear as ClearIcon, RestoreFromTrash as RestoreIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { Beneficiaire } from '../../types/beneficiaire';
import { beneficiairesAPI } from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import { getModuleTableTheme } from '../../theme/tableTheme';
import CustomTablePagination from '../../components/CustomTablePagination';
import PermissionButton from '../../components/PermissionButton';
import { extractErrorMessage } from '../../utils/errorHandler';
import BeneficiaireFormDialog from './BeneficiaireFormDialog';
import ColumnFilter from '../../components/ColumnFilter';
import { useColumnFilters } from '../../hooks/useColumnFilters';

const FINANCIER_COLOR = '#9C27B0';

const TYPE_LABELS: Record<string, string> = {
  employee: 'Employé',
  individual: 'Individuel',
  other: 'Autre',
};

const BENEFICIAIRE_FILTER_SCHEMA = {
  name:  { type: 'text',   param: 'name' },
  type:  { type: 'select', param: 'type' },
  phone: { type: 'text',   param: 'phone' },
  email: { type: 'text',   param: 'email' },
  rib:   { type: 'text',   param: 'rib' },
} as const;

const BeneficiaireList: React.FC = () => {
  const [beneficiairesList, setBeneficiairesList] = useState<Beneficiaire[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletedList, setDeletedList] = useState<Beneficiaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [editId, setEditId] = useState<number | undefined>(undefined);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    BENEFICIAIRE_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchBeneficiaires = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      // `type` is a typed param the API helper already accepts; everything
      // else flows through the extras bag.
      const { type, ...extra } = toQueryParams() as Record<string, any>;
      const response = await beneficiairesAPI.getPaginated(
        skip,
        rowsPerPage,
        debouncedSearch || undefined,
        (type as string) || undefined,
        Object.keys(extra).length > 0 ? extra : undefined,
      );
      setBeneficiairesList(response.items);
      setTotalCount(response.total);
    } catch (err) {
      showError('Erreur lors du chargement des bénéficiaires');
      setBeneficiairesList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const fetchDeleted = async () => {
    try {
      setLoading(true);
      const data = await beneficiairesAPI.getDeleted();
      setDeletedList(data);
    } catch (err) {
      showError('Erreur lors du chargement des bénéficiaires supprimés');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tabValue === 0) fetchBeneficiaires();
  }, [fetchBeneficiaires, tabValue]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) fetchDeleted();
  };

  const handleDelete = async (item: Beneficiaire) => {
    showConfirm(`Êtes-vous sûr de vouloir supprimer "${item.name}" ?`, async () => {
      try {
        await beneficiairesAPI.delete(item.id);
        showSuccess('Bénéficiaire supprimé avec succès');
        fetchBeneficiaires();
      } catch (err: any) {
        showError(extractErrorMessage(err));
      }
    });
  };

  const handleRestore = async (item: Beneficiaire) => {
    showConfirm(`Êtes-vous sûr de vouloir restaurer "${item.name}" ?`, async () => {
      try {
        await beneficiairesAPI.restore(item.id);
        showSuccess('Bénéficiaire restauré avec succès');
        fetchDeleted();
        fetchBeneficiaires();
      } catch (err: any) {
        showError(extractErrorMessage(err));
      }
    });
  };

  const handleAdd = () => { setEditId(undefined); setFormMinimized(false); setFormDialogOpen(true); };
  const handleEdit = (item: Beneficiaire) => { setEditId(item.id); setFormMinimized(false); setFormDialogOpen(true); };
  const handleFormSuccess = () => { setFormDialogOpen(false); setFormMinimized(false); fetchBeneficiaires(); };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" color="primary">Gestion des Bénéficiaires</Typography>
        <PermissionButton moduleName="Config" operation="add" miniModuleName="Beneficiaires" variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Nouveau Bénéficiaire
        </PermissionButton>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Bénéficiaires actifs" />
        <Tab label="Corbeille" />
      </Tabs>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Rechercher..." value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon /></IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        {tabValue === 0 && hasActive() && (
          <Button
            size="small"
            startIcon={<FilterListOffIcon />}
            onClick={reset}
            sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            Effacer les filtres
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={getModuleTableTheme('financier')}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Nom
                  {tabValue === 0 && <ColumnFilter type="text" value={filters.name} onChange={(v) => update('name', v)} label="nom" color={FINANCIER_COLOR} />}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Type
                  {tabValue === 0 && (
                    <ColumnFilter
                      type="select"
                      value={filters.type}
                      onChange={(v) => update('type', v)}
                      label="type"
                      color={FINANCIER_COLOR}
                      options={[
                        { value: 'employee', label: 'Employé' },
                        { value: 'individual', label: 'Individuel' },
                        { value: 'other', label: 'Autre' },
                      ]}
                    />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Téléphone
                  {tabValue === 0 && <ColumnFilter type="text" value={filters.phone} onChange={(v) => update('phone', v)} label="téléphone" color={FINANCIER_COLOR} />}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Email
                  {tabValue === 0 && <ColumnFilter type="text" value={filters.email} onChange={(v) => update('email', v)} label="email" color={FINANCIER_COLOR} />}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  RIB
                  {tabValue === 0 && <ColumnFilter type="text" value={filters.rib} onChange={(v) => update('rib', v)} label="RIB" color={FINANCIER_COLOR} />}
                </Box>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(tabValue === 0 ? beneficiairesList : deletedList).map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.name}</TableCell>
                <TableCell>
                  <Chip label={TYPE_LABELS[item.type] || item.type} size="small" color="primary" variant="outlined" />
                </TableCell>
                <TableCell>{item.phone || '-'}</TableCell>
                <TableCell>{item.email || '-'}</TableCell>
                <TableCell>{item.rib || '-'}</TableCell>
                <TableCell align="center">
                  {tabValue === 0 ? (
                    <>
                      <Tooltip title="Modifier">
                        <IconButton size="small" onClick={() => handleEdit(item)} color="primary"><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" onClick={() => handleDelete(item)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="Restaurer">
                      <IconButton size="small" onClick={() => handleRestore(item)} color="success"><RestoreIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(tabValue === 0 ? beneficiairesList : deletedList).length === 0 && (
              <TableRow><TableCell colSpan={6} align="center">Aucun bénéficiaire trouvé</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {tabValue === 0 && (
        <CustomTablePagination
          count={totalCount} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      )}

      <BeneficiaireFormDialog open={formDialogOpen} onClose={() => { setFormDialogOpen(false); setFormMinimized(false); }} onSuccess={handleFormSuccess} editId={editId} minimized={formMinimized} onMinimizedChange={setFormMinimized} />
      <AlertDialog {...alertState} onClose={hideAlert} onConfirm={alertState.onConfirm || hideAlert} />
    </Box>
  );
};

export default BeneficiaireList;
